import { BRAND } from './brand.js'
import { apiOrigin, currentBearerToken } from './trpc.js'

/**
 * The URL of an attachment's bytes, or null when there are none yet.
 *
 * `checksum` is the readiness flag, and it is one the transfer phase sets only
 * after the bytes are safely in storage. During the dual run most of the library
 * is unfetched, so "no URL" is the ordinary case and callers draw a placeholder
 * rather than a broken image.
 *
 * On the web build this is a same-origin path and the session cookie
 * authenticates it. In the Capacitor build there is no first-party cookie and an
 * `<img>` cannot send an Authorization header, so the token rides in the query
 * — the one place we accept that, and the reason `readSessionToken` takes an
 * opt-in flag rather than always looking there.
 */
export function fileUrl(file: { id: string; checksum: string | null }): string | null {
  if (!file.checksum) return null

  const token = currentBearerToken()
  const query = token ? `?token=${encodeURIComponent(token)}` : ''

  return `${apiOrigin}/files/${file.id}${query}`
}

/** The same file, asked for as a download rather than shown in the browser. */
export function downloadUrl(file: { id: string; checksum: string | null }): string | null {
  const url = fileUrl(file)
  return url ? asDownload(url) : null
}

/** A file URL from `fileUrl`, asked for as a download. */
export function asDownload(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}download=1`
}

/**
 * The business's logo, which `POST /files/logo` writes onto Settings rather
 * than onto a job. PNG or JPEG: the PDF embeds nothing else. Decision 81.
 */
export async function uploadLogo(file: File | Blob): Promise<{ logoUrl: string }> {
  const body = new FormData()
  body.append('file', file, file instanceof File ? file.name : 'logo.png')
  const token = currentBearerToken()

  const response = await fetch(`${apiOrigin}/files/logo`, {
    method: 'POST',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
    body,
  })

  if (!response.ok) {
    const reason = await response
      .json()
      .then((payload: { error?: string }) => payload.error)
      .catch(() => null)
    throw new Error(reason ?? `The logo did not upload (${response.status}).`)
  }

  return (await response.json()) as { logoUrl: string }
}

export async function removeLogo(): Promise<void> {
  const token = currentBearerToken()
  const response = await fetch(`${apiOrigin}/files/logo`, {
    method: 'DELETE',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
  })
  if (!response.ok) throw new Error(`The logo was not removed (${response.status}).`)
}

/**
 * The letterhead logo: the uploaded one, made absolute for the native build,
 * or the supplied artwork until the office uploads one — the same fallback the
 * PDFs print (ADR 024).
 */
export function logoSrc(logoUrl: string | null | undefined): string {
  return logoUrl ? `${apiOrigin}${logoUrl}` : BRAND.artwork.horizontal
}

export type UploadedFile = {
  id: string
  kind: string
  filename: string
  mimeType: string
  sizeBytes: number
  checksum: string | null
}

/**
 * Sends one file to `POST /files` and hands back the `Attachment` it became.
 *
 * Not a tRPC mutation, and not for want of trying: tRPC serialises its input as
 * JSON, so a photo would have to be base64 in a request body — a third more
 * bytes over the connection that least affords them, and the whole 12 MB of it
 * buffered as a string on both sides. `fetch` with a `FormData` streams.
 *
 * Credentials go the same two ways as the download: the cookie on the web build,
 * the bearer token in the native one.
 */
export async function uploadFile(
  file: File | Blob,
  options: {
    /**
     * The job it belongs to, or `scope: 'licence'` for a photographed card, or
     * `scope: 'proposal'` for a photograph in a proposal template.
     */
    jobId?: string
    scope?: 'licence' | 'proposal'
    filename?: string
    kind?: 'PHOTO' | 'SIGNATURE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO'
  },
): Promise<UploadedFile> {
  const body = new FormData()
  if (options.jobId) body.append('jobId', options.jobId)
  if (options.scope) body.append('scope', options.scope)
  if (options.kind) body.append('kind', options.kind)
  body.append('file', file, options.filename ?? (file instanceof File ? file.name : 'upload'))

  const token = currentBearerToken()

  const response = await fetch(`${apiOrigin}/files`, {
    method: 'POST',
    credentials: 'include',
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
    body,
  })

  if (!response.ok) {
    const reason = await response
      .json()
      .then((payload: { error?: string }) => payload.error)
      .catch(() => null)

    throw new Error(reason ?? `That file did not upload (${response.status}).`)
  }

  return (await response.json()) as UploadedFile
}
